import './LegalPage.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../Navigation/Navbar';
import React from 'react';

function LegalDocBlock({ block }) {
    switch (block.type) {
        case 'h2':
            return <h2>{block.text}</h2>;
        case 'p':
            return <p>{block.text}</p>;
        case 'ul':
            return (
                <ul>
                    {block.items.map((item, idx) => <li key={idx}>{item}</li>)}
                </ul>
            );
        case 'ulStrong':
            return (
                <ul>
                    {block.items.map((item, idx) => (
                        <li key={idx}><strong>{item.strong}</strong> {item.text}</li>
                    ))}
                </ul>
            );
        default:
            return null;
    }
}

const DOC_META = [
    {
        id: 'doc-privacy',
        docKey: 'privacy',
        menuKey: 'privacy',
        group: 'general',
        active: true,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-3z"/></svg>,
    },
    {
        id: 'doc-cookie',
        docKey: 'cookie',
        menuKey: 'cookie',
        group: null,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="9" cy="9.5" r="1"/><circle cx="14.5" cy="8.5" r="1"/><circle cx="15" cy="14" r="1"/><circle cx="9.5" cy="14.5" r="1"/></svg>,
    },
    {
        id: 'doc-terms',
        docKey: 'terms',
        menuKey: 'terms',
        group: null,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4"/><path d="M9.5 12.5h5M9.5 15.5h5"/></svg>,
    },
    {
        id: 'doc-admin',
        docKey: 'admin',
        menuKey: 'admin',
        group: 'institutional',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 13.5a1.7 1.7 0 000-3l1-1.7-1.5-1.5-1.7 1a1.7 1.7 0 00-3 0l-1.7-1-1.5 1.5 1 1.7a1.7 1.7 0 000 3l-1 1.7 1.5 1.5 1.7-1a1.7 1.7 0 003 0l1.7 1 1.5-1.5-1-1.7z"/></svg>,
    },
    {
        id: 'doc-school',
        docKey: 'school',
        menuKey: 'school',
        group: null,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M5 21V10l7-5 7 5v11M9 21v-6h6v6"/></svg>,
    },
    {
        id: 'doc-funds',
        docKey: 'funds',
        menuKey: 'funds',
        group: null,
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20V10M10 20V4M16 20v-7M4 20h16"/></svg>,
    },
];

function LegalPage () {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const showLegalDoc = (docId) => {
      // Hide all documents
      document.querySelectorAll('.legal-doc-section').forEach(doc => {
        doc.classList.remove('active');
      });
      // Show targeted document
      document.getElementById(docId).classList.add('active');

      // Update sidebar active class
      document.querySelectorAll('.legal-sidebar-menu li').forEach(li => {
        li.classList.remove('active');
      });
      document.getElementById('menu-' + docId).classList.add('active');

      // Auto scroll to top if on mobile
      if(window.innerWidth <= 900) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    return (
        <>
        <Navbar />
        <div id="legalPage" className="legal-page">
            <div className="legal-container">
            <div className="legal-sidebar">
                <div className="legal-sidebar-panel">
                <div className="legal-sidebar-header">
                    <p className="legal-sidebar-eyebrow">{t('legalPage.sidebarEyebrow')}</p>
                    <h3>{t('legalPage.sidebarTitle')}</h3>
                </div>
                <ul className="legal-sidebar-menu">
                    {DOC_META.map((doc) => (
                        <React.Fragment key={doc.id}>
                            {doc.group === 'general' && <li className="legal-sidebar-group-label">{t('legalPage.generalGroup')}</li>}
                            {doc.group === 'institutional' && <li className="legal-sidebar-group-label">{t('legalPage.institutionalGroup')}</li>}
                            <li id={`menu-${doc.id}`} className={doc.active ? 'active' : undefined} onClick={() => showLegalDoc(doc.id)}>
                                <span className="icon">{doc.icon}</span>
                                <span>{t(`legalPage.menu.${doc.menuKey}`)}</span>
                            </li>
                        </React.Fragment>
                    ))}
                </ul>
                </div>
            </div>
            <div className="legal-content">
                {DOC_META.map((doc) => {
                    const content = t(`legalPage.docs.${doc.docKey}.content`, { returnObjects: true });
                    return (
                        <div key={doc.id} id={doc.id} className={`legal-doc-section${doc.active ? ' active' : ''}`}>
                            <h1>{t(`legalPage.docs.${doc.docKey}.title`)}</h1>
                            <p>{t('legalPage.lastUpdated')}</p>
                            {content.map((block, idx) => <LegalDocBlock key={idx} block={block} />)}
                        </div>
                    );
                })}
            </div>
            </div>

            <footer className="footer-main" style={{marginTop: '0'}}>
                <div className="footer-left">
                    <div className="footer-logo">MindfulCare</div>
                    <p>{t('footer.tagline')}</p>
                </div>

                <div className="footer-center">
                    <a onClick={() => navigate('/privacy-policy')} className="footer-link">{t('footer.legalPolicies')}</a>
                    <p>{t('footer.contactPrefix')} <a href="mailto:MindfulCare@gmail.com" style={{color: 'rgba(255,255,255,0.7)', textDecoration: 'none'}}>MindfulCare@gmail.com</a></p>
                </div>

                <div className="footer-right">
                    <p>{t('footer.copyright')}</p>
                </div>
            </footer>
        </div>
        </>
    );
}

export default LegalPage;
